// src/context/TenantContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import supabase from "../supabaseClient";

const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);       // datos de la empresa
  const [perfil, setPerfil] = useState(null);        // profile del usuario (rol, permisos)
  const [cargando, setCargando] = useState(true);    // loading inicial
  const [error, setError] = useState(null);
  const cargaInicialCompleta = React.useRef(false);

  // Cargar perfil y tenant del usuario autenticado
  // Si softRefresh=true, no muestra loading (evita desmontar la UI)
  const cargarTenant = useCallback(async (softRefresh = false) => {
    try {
      if (!softRefresh) {
        setCargando(true);
      }
      setError(null);

      // 1. Obtener usuario autenticado
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setTenant(null);
        setPerfil(null);
        setCargando(false);
        return;
      }

      // 2. Obtener profile (tiene tenant_id y rol)
      const { data: perfilData, error: perfilError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (perfilError || !perfilData) {
        console.error("Error cargando perfil:", perfilError);
        setError("No se encontró perfil de usuario");
        setCargando(false);
        return;
      }

      // 🔒 Perfil desactivado o borrado por su administrador.
      // RLS ya le niega todos los datos; sin esto vería la app vacía sin explicación.
      // Nota: se usa chequeo "truthy" en deleted_at a propósito — si la columna
      // llegara undefined, NO debe interpretarse como borrado.
      if (perfilData.activo === false || perfilData.deleted_at) {
        // Páginas de marketing: si quedó una sesión muerta en el navegador,
        // se limpia EN SILENCIO. Un visitante cualquiera de la landing no tiene
        // por qué ver un aviso de administradores.
        const paginasMarketing = ["/", "/terminos", "/faq", "/registro", "/reset-password"];
        const enMarketing = paginasMarketing.includes(window.location.pathname);

        setTenant(null);
        setPerfil(null);
        setCargando(false);
        cargaInicialCompleta.current = false;
        localStorage.removeItem("usuario");
        await supabase.auth.signOut();

        if (!enMarketing) {
          window.alert(
            "Tu acceso a esta empresa fue desactivado por el administrador.\n\n" +
            "Si crees que es un error, comunícate con el administrador de tu empresa."
          );
          // Si ya está en /login, no recargar: dejarlo ahí para que reintente.
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return;
      }

      // 3. Obtener datos del tenant (empresa)
      const { data: tenantData, error: tenantError } = await supabase
        .from("tenants")
        .select("*")
        .eq("id", perfilData.tenant_id)
        .single();

      if (tenantError || !tenantData) {
        console.error("Error cargando tenant:", tenantError);
        setError("No se encontró la empresa");
        setCargando(false);
        return;
      }

      setPerfil(perfilData);
      setTenant(tenantData);
      cargaInicialCompleta.current = true;
      setCargando(false);

    } catch (err) {
      console.error("Error en TenantContext:", err);
      setError("Error cargando datos de empresa");
      setCargando(false);
    }
  }, []);

  // Escuchar cambios de sesión (login/logout/refresh)
  useEffect(() => {
    cargarTenant();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Si ya completó carga inicial, es un refresh de token — no desmontar UI
        cargarTenant(cargaInicialCompleta.current);
      }
      if (event === "SIGNED_OUT") {
        setTenant(null);
        setPerfil(null);
        setCargando(false);
        setError(null);
        cargaInicialCompleta.current = false;
        // Si perdimos la sesión y no estamos en una página pública, ir a login
        const rutasPublicas = ["/", "/login", "/registro", "/terminos", "/faq", "/reset-password"];
        if (!rutasPublicas.includes(window.location.pathname)) {
          localStorage.removeItem("usuario");
          window.location.href = "/login";
        }
      }
    });

    // 🔑 Vigilante de sesión: al volver a la pestaña, validar/refrescar el token.
    // Evita el "congelamiento" cuando el JWT expiró estando en otra pestaña.
    const revalidarSesion = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return; // No hay sesión local; el flujo normal de login se encarga
        // Si el token expira en menos de 60s o ya expiró, refrescar proactivamente
        const expiraEnMs = (session.expires_at || 0) * 1000 - Date.now();
        if (expiraEnMs < 60000) {
          const { error: errRefresh } = await supabase.auth.refreshSession();
          if (errRefresh) {
            // No se pudo refrescar: sesión inválida → salir limpio
            const rutasPublicas = ["/", "/login", "/registro", "/terminos", "/faq", "/reset-password"];
            if (!rutasPublicas.includes(window.location.pathname)) {
              localStorage.removeItem("usuario");
              await supabase.auth.signOut();
              window.location.href = "/login";
            }
          }
        }
      } catch (e) {
        console.error("Error revalidando sesión:", e);
      }
    };

    document.addEventListener("visibilitychange", revalidarSesion);
    window.addEventListener("focus", revalidarSesion);

    return () => {
      subscription?.unsubscribe();
      document.removeEventListener("visibilitychange", revalidarSesion);
      window.removeEventListener("focus", revalidarSesion);
    };
  }, [cargarTenant]);

  // Helpers útiles
  const esAdmin = perfil?.rol === "admin" || perfil?.rol === "super_admin";
  const esSuperAdmin = perfil?.rol === "super_admin";
  const tenantActivo = tenant?.estado === "activo";

  return (
    <TenantContext.Provider
      value={{
        tenant,          // { id, nombre, slug, logo_url, direccion, ... }
        perfil,          // { id, tenant_id, nombre, rol, permisos }
        cargando,
        error,
        esAdmin,
        esSuperAdmin,
        tenantActivo,
        recargar: cargarTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);